import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useProduct } from "src/context/ProductProvider";
import { useIsMobile } from "src/hooks/useIsMobile";
import { Button, Divider, lighten, Stack, Typography } from "@mui/material";
import { handleFocusIntoView } from "src/utils/focusIntoView";
import { theme } from "src/assets/theme";
import { Coupon } from "src/api/types";
import Header from "../Onboarding/Payment/components/Headers/Header";
import Product from "../Onboarding/Payment/components/Product";
import Subheader from "../Onboarding/Payment/components/Headers/Subheader";
import PaymentProcessor from "../Onboarding/Payment/components/PaymentProcessor";
import TermsOfConditions from "../Onboarding/Payment/components/TermsOfConditions";
import ApplyCoupon from "../Onboarding/Payment/components/ApplyCoupon";
import { useSelectedProduct } from "src/hooks/useSelectedProduct";
import { useGetPatientCouponsQuery } from "src/api/services/patientCouponsApi";
import CouponView from "../Onboarding/Payment/components/CouponView";
import InformationArray from "../Onboarding/Payment/components/InformationArray";

const SkinCarePaymentInformation = () => {
  const isMobile = useIsMobile();
  const [selectedProduct] = useSelectedProduct();
  const productSlug = useProduct((p) => p.slug);
  const page = "payment-information";
  const { t } = useTranslation();
  const isGlp1 = useProduct((product) => product.is_glp1);
  const billingModel = useProduct((product) => product.billing_model);
  const [coupon, setCoupon] = useState<Coupon | null>(null);
  const { data: coupons = [] } = useGetPatientCouponsQuery({
    product_slug: productSlug,
    redeemed: false,
  });

  const discount = useMemo(() => {
    const additionalDiscount = coupon?.amount ?? 0;

    return coupons.length > 0
      ? coupons.reduce((acc, c) => (acc += c.amount) - additionalDiscount, 0)
      : additionalDiscount;
  }, [coupon?.amount, coupons]);

  const defaultProductPrice = useMemo(() => {
    if (selectedProduct == null) {
      return "0";
    }

    const price = Math.max(
      selectedProduct?.amount - discount,
      selectedProduct.amount_limit ?? 0,
    );

    return price.toLocaleString();
  }, [discount, selectedProduct]);

  const saving = useMemo(() => {
    if (selectedProduct == null) return;
    const defaultPrice = selectedProduct.amount;

    const comparedPrice = selectedProduct.competitor_price || 0;

    return comparedPrice - defaultPrice + discount;
  }, [discount, selectedProduct]);

  return (
    <Stack gap={3}>
      <Stack gap={2}>
        <Stack gap={3}>
          <Header />
          <Product product={selectedProduct} />
        </Stack>
        <Stack gap={2} onClick={() => handleFocusIntoView("payment-input")}>
          <Typography variant={isMobile ? "body1" : "h6"} fontWeight={600}>
            <Subheader />
          </Typography>
          <InformationArray />
        </Stack>
      </Stack>
      <Stack gap={3}>
        <Stack gap={4}>
          <Stack gap={2}>
            <Stack gap={1}>
              <ApplyCoupon setCoupon={setCoupon} coupon={coupon} />
              {coupons.map((c) => (
                <CouponView key={c.name} coupon={c} />
              ))}
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography fontSize={"22px"} fontWeight={600}>
                  {t(`${page}.total`)}
                </Typography>
                <Typography fontSize={"22px"} fontWeight={600}>
                  ${defaultProductPrice}
                </Typography>
              </Stack>
              <Typography
                color={theme.palette.primary.main}
                fontStyle="italic"
                fontWeight={600}
                fontSize={"18px"}
              >
                {t(`${page}.${billingModel}.disclaimer`)}
              </Typography>
            </Stack>
            {saving != null && saving > 0 ? (
              <Button
                variant="text"
                sx={{
                  backgroundColor: lighten(theme.palette.secondary.main, 0.3),
                  boxShadow: "0px 4px 4px 0px rgba(0, 0, 0, 0.25)",
                  color: "#ffffff",
                  fontSize: "16px",
                  fontWeight: 700,
                  borderRadius: "10px",
                  textAlign: "center",
                }}
                onClick={() => handleFocusIntoView("payment-input")}
              >
                {t(`${page}.totalSavings`, {
                  saving,
                })}
              </Button>
            ) : null}
            <Divider sx={{ position: "relative", top: 15 }} />
          </Stack>
          {selectedProduct ? (
            <PaymentProcessor
              product={selectedProduct}
              coupon={coupon}
              dueToday={Number(defaultProductPrice)}
            />
          ) : null}
        </Stack>
      </Stack>
      <TermsOfConditions
        page={page}
        productSlug={productSlug}
        isGlp1={isGlp1}
        numberOfMonths={selectedProduct ? selectedProduct.number_of_months : 1}
      />
    </Stack>
  );
};

export default SkinCarePaymentInformation;
